import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { HeroVideo } from "@/components/HeroVideo";
import { ProductCard } from "@/components/ProductCard";

export const PHILOSOPHY_FALLBACK = "https://images.unsplash.com/photo-1638444090472-b72f11195f9b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const Marquee = ({ text }) => (
  <div className="border-y border-white/10 py-5 overflow-hidden whitespace-nowrap">
    <div className="marquee inline-block">
      {Array.from({ length: 2 }).map((_, k) => (
        <span key={k} className="font-serif-display text-2xl uppercase tracking-[0.2em] text-white/80 mx-8">
          {text} ·&nbsp;
        </span>
      ))}
    </div>
  </div>
);

export default function Home() {
  const { homepage } = useCurrency();
  const [hero, setHero] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    api.get("/hero").then((r) => setHero(r.data || {})).catch(() => {});
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products").then((r) => setLatest(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <HeroVideo hero={hero} />
      <Marquee text={homepage?.marquee || "New Arrivals · Made to Endure · Editorial Tailoring · Quiet Luxury"} />

      {/* Latest Drop */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-4">{homepage?.latest_eyebrow || "01 — Latest Drop"}</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light uppercase tracking-tight">
              {homepage?.latest_title || "New Arrivals"}
            </h2>
          </div>
          <Link
            to="/collection"
            data-testid="view-all-btn"
            className="font-mono-label text-xs uppercase tracking-luxe text-white/60 hover:text-white border-b border-white/30 pb-1 self-start"
          >
            View Full Collection →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
          {(featured.length ? featured : latest).slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Editorial split */}
      <section className="grid grid-cols-1 md:grid-cols-2 items-stretch">
        <div className="bg-neutral-900 aspect-[4/5] md:aspect-auto overflow-hidden">
          <img
            data-testid="philosophy-image"
            src={homepage?.philosophy_image ? mediaUrl(homepage.philosophy_image) : PHILOSOPHY_FALLBACK}
            alt="Atelier editorial"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center px-8 lg:px-20 py-20">
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-6" data-testid="philosophy-section-name">02 — {homepage?.philosophy_label || "The Philosophy"}</p>
          <h2 className="font-serif-display text-4xl lg:text-5xl font-light leading-tight mb-8">
            {homepage?.philosophy_title || "Garments that speak in a whisper, not a shout."}
          </h2>
          <p className="text-white/60 leading-relaxed max-w-md">
            {homepage?.philosophy_text || "Every piece begins in our studio as a single line. We refine until nothing remains but the essential — form, weight, and the quiet confidence of restraint."}
          </p>
        </div>
      </section>

      {/* Rest of collection */}
      {latest.length > 4 && (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-4">03 — The Collection</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light uppercase tracking-tight mb-14">
            Explore More
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
            {latest.slice(4, 8).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
